import { useCallback, useEffect, useRef, useState } from "react"

export type SubmenuPosition = "left" | "right"

type UseSubmenuPositioningOptions = {
  submenuWidth?: number
  gap?: number
  trigger?: boolean // Whether positioning should be active
}

/**
 * Hook to calculate optimal submenu positioning based on viewport space
 * Prevents submenus from overflowing the viewport by positioning them left or right
 */
export function useSubmenuPositioning({
  submenuWidth = 256,
  gap = 4,
  trigger = false,
}: UseSubmenuPositioningOptions = {}) {
  const [position, setPosition] = useState<SubmenuPosition>("right")
  const menuRef = useRef<HTMLDivElement>(null)

  const calculatePosition = useCallback(() => {
    if (!menuRef.current) return

    const rect = menuRef.current.getBoundingClientRect()
    const spaceOnRight = window.innerWidth - rect.right

    // Add 20px buffer for safety
    if (spaceOnRight < submenuWidth + gap + 20) {
      setPosition("left")
    } else {
      setPosition("right")
    }
  }, [submenuWidth, gap])

  useEffect(() => {
    if (trigger) {
      calculatePosition()
    }
  }, [trigger, calculatePosition])

  // Generate Tailwind classes based on position
  const getSubmenuClasses = useCallback(
    (baseClasses: string = "") => {
      const positionClasses =
        position === "right" ? "absolute left-full top-0 ml-1" : "absolute right-full top-0 mr-1"

      return `${positionClasses} ${baseClasses}`.trim()
    },
    [position]
  )

  return {
    position,
    menuRef,
    calculatePosition,
    getSubmenuClasses,
  }
}
